import { routePaths } from "@/App";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useTourDetailContext } from "../data/provider";
import { InternalTourStop } from "../types";
import { isTourStopProcessable } from "../utils/shared";
import { useSettings } from "@/libs/settings/hooks/useSettings";
import { OrganizationSettings } from "@/libs/api/types";

export function useTourNavigation(currentStop: InternalTourStop | undefined) {
  const navigate = useNavigate();

  const { tour, stops } = useTourDetailContext();

  const { getSettingValue } = useSettings();

  const canProcessTourStop = useCallback(
    (tourStop?: InternalTourStop | undefined) => {
      const forceOrderDelivery = getSettingValue(
        OrganizationSettings.FORCE_IN_ORDER_DELIVERY,
      );

      return isTourStopProcessable({
        currentStop: tourStop ?? currentStop,
        tourState: tour.state,
        shouldProcessInOrder: forceOrderDelivery,
        stops,
      });
    },
    [currentStop, getSettingValue, stops, tour.state],
  );

  const navigateToStop = useCallback(() => {
    if (!currentStop || !canProcessTourStop(currentStop)) {
      return;
    }

    navigate(
      routePaths.createTourStopPath(tour.id, currentStop.id, currentStop.type),
    );
  }, [canProcessTourStop, currentStop, navigate, tour.id]);

  const navigateToNextStop = useCallback(() => {
    if (!currentStop) {
      return;
    }

    const currentIndex = stops.findIndex((stop) => stop.id === currentStop.id);

    if (currentIndex === stops.length - 1) {
      navigate(routePaths.createTourPath(tour.id));
      return;
    }

    const nextStop = stops[currentIndex + 1];
    const canNavigateToNextStop = canProcessTourStop(nextStop);

    if (!canNavigateToNextStop) {
      return;
    }

    navigate(
      routePaths.createTourStopPath(tour.id, nextStop.id, nextStop.type),
    );
  }, [canProcessTourStop, currentStop, navigate, stops, tour.id]);

  return useMemo(
    () => ({
      isTourStopAccessible: canProcessTourStop(),
      navigateToStop,
      navigateToNextStop,
    }),
    [canProcessTourStop, navigateToNextStop, navigateToStop],
  );
}
